import React, { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Loader2, MessageCircle, Send, Search, Flame, Snowflake, Lock, ExternalLink,
  Check, CheckCheck, AlertTriangle, Inbox as InboxIcon, RefreshCw, FileText,
  Image as ImageIcon, Mic, Video, Paperclip, CornerUpLeft, X, Smile,
  ChevronLeft, ChevronRight, Download, LayoutGrid, Trash2, UserPlus, User,
  Clock, CalendarClock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/formatUtils';
import { useConfirmation } from '../../components/ConfirmationProvider';
import ChatLightbox from '../../components/smb/ChatLightbox';
import { LinkifiedText } from '../../components/smb/LinkifiedText';
import { useInboxNotify } from '../../context/InboxNotifyContext';
import { SmbOutreachAutomationBanner } from '../../components/smb/SmbOutreachAutomationBanner';

// Lazy-load the emoji picker so it doesn't bloat the main bundle (~250KB raw).
const EmojiPicker = lazy(() => import('emoji-picker-react'));

// Polling cadence — dynamic based on conversation state and tab visibility:
//   • Tab focused + Live Chat active (24h window):   3s
//   • Tab focused + template-only (cold thread):    15s
//   • Tab hidden (browser throttles to 60s anyway): 30s
const POLL_ACTIVE_MS = 3000;
const POLL_INACTIVE_MS = 15000;
const POLL_HIDDEN_MS = 30000;

// Absolute URL for media files served from /api/uploads/whatsapp/...
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveMediaUrl = (u) => {
  if (!u) return null;
  // Pass through absolute URLs and blob:/data: URIs (used by optimistic outbound).
  if (/^(https?:|blob:|data:)/i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

const MediaAttachment = ({ msg, onImageClick }) => {
  const src = resolveMediaUrl(msg.media_url);
  const kind = msg.kind;
  if (!src) {
    // Media metadata is in the message but the download/save failed.
    const label = {
      image: 'Image', audio: 'Audio', voice: 'Voice message',
      video: 'Video', document: 'Document', sticker: 'Sticker',
    }[kind] || 'Attachment';
    return (
      <div className="flex items-center gap-2 text-xs italic opacity-80" data-testid={`msg-media-missing-${msg.id}`}>
        <AlertTriangle className="h-3.5 w-3.5" />
        {label} unavailable
      </div>
    );
  }
  if (kind === 'image' || kind === 'sticker') {
    return (
      <button
        type="button"
        onClick={() => onImageClick && onImageClick(msg)}
        className="block focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg"
        data-testid={`msg-media-img-${msg.id}`}
        title="Click to view"
      >
        <img src={src} alt={msg.caption || kind} className="max-w-[280px] max-h-[280px] rounded-lg object-cover cursor-zoom-in" />
      </button>
    );
  }
  if (kind === 'audio' || kind === 'voice') {
    return (
      <audio controls src={src} className="max-w-[260px]" data-testid={`msg-media-audio-${msg.id}`} />
    );
  }
  if (kind === 'video') {
    return (
      <video controls src={src} className="max-w-[280px] max-h-[280px] rounded-lg" data-testid={`msg-media-video-${msg.id}`} />
    );
  }
  if (kind === 'document') {
    const name = msg.media_filename || 'Document';
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        download={name}
        className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/40 hover:bg-muted/60 text-sm"
        data-testid={`msg-media-doc-${msg.id}`}
      >
        <Paperclip className="h-4 w-4 shrink-0" />
        <span className="truncate max-w-[220px]">{name}</span>
      </a>
    );
  }
  return null;
};

const KindIcon = ({ kind }) => {
  if (kind === 'image' || kind === 'sticker') return <ImageIcon className="h-3 w-3" />;
  if (kind === 'audio' || kind === 'voice') return <Mic className="h-3 w-3" />;
  if (kind === 'video') return <Video className="h-3 w-3" />;
  if (kind === 'document') return <Paperclip className="h-3 w-3" />;
  return null;
};

const TempBadge = ({ t }) => {
  if (!t || t === 'unknown') return null;
  const map = {
    hot: { cls: 'bg-red-500/15 text-red-400 border-red-500/30', icon: <Flame className="h-3 w-3 mr-1" /> },
    warm: { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: null },
    cold: { cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30', icon: <Snowflake className="h-3 w-3 mr-1" /> },
  };
  const cfg = map[t] || { cls: 'bg-muted text-muted-foreground', icon: null };
  return <Badge className={`${cfg.cls} text-[10px] py-0 px-1.5`}>{cfg.icon}{t.toUpperCase()}</Badge>;
};

const StatusIcon = ({ status }) => {
  if (status === 'failed') return <AlertTriangle className="h-3 w-3 text-red-500" />;
  if (status === 'sent') return <Check className="h-3 w-3 text-gray-500" />;
  if (status === 'delivered') return <CheckCheck className="h-3 w-3 text-gray-500" />;
  if (status === 'read') return <CheckCheck className="h-3 w-3 text-sky-500" />;
  return null;
};

const SMBInboxPage = () => {
  // Compact initials helper for the "handled by" pill in the conversation
  // list. "Amit Sharma" → "AS"; single word → first 2 chars; empty → "?".
  const initialsOf = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirmation();
  const { refresh: refreshInboxNotify, leadCount: globalUnreadLeadCount } = useInboxNotify();
  const { hasPermission } = useAuth();
  // Permissions: SMB chats vs General chats are independently grantable.
  // Legacy `smb.inbox` still acts as an alias for "both" on the backend.
  const canSeeSmb = hasPermission('whatsapp.inbox.smb') || hasPermission('smb.inbox');
  const canSeeGeneral = hasPermission('whatsapp.inbox.general') || hasPermission('smb.inbox');
  const canSeeBoth = canSeeSmb && canSeeGeneral;
  // Default the chat-type filter to the only side the user is allowed to see.
  const defaultChatType = canSeeBoth ? 'all' : (canSeeSmb ? 'smb' : 'general');
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState('all'); // all | active | inactive | mine | follow_ups | unread | hot
  const [chatType, setChatType] = useState(defaultChatType); // all | smb | general
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [followUpSummary, setFollowUpSummary] = useState({ overdue: 0, due_today: 0, this_week: 0, total_pending: 0 });
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpAt, setFollowUpAt] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpSaving, setFollowUpSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const loadMoreRef = useRef(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [tplKey, setTplKey] = useState('');
  const [replyTo, setReplyTo] = useState(null); // message obj being replied to
  const [pendingMessages, setPendingMessages] = useState([]); // optimistic outbound, keyed by tempId
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // {file, previewUrl}
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ business_name: '', category: '', contact_name: '' });
  const [converting, setConverting] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
  const [refreshingSessions, setRefreshingSessions] = useState(false);
  const PAGE_SIZE = 100;
  // Ref to the AbortController for the in-flight sessions fetch. When the
  // user switches tabs while a previous request is in-flight, we cancel
  // it so the response from the OLD tab can never race-overwrite the new
  // tab's data. Also lets polling pause cleanly during a tab switch (G).
  const sessionsAbortRef = useRef(null);
  // Tracks whether we've ever loaded sessions for THIS filter+chatType
  // combo. While `false`, we show the full skeleton; while `true`, we
  // keep the previous list visible and just show a thin top progress
  // strip — feels instant even when backend takes 200-300ms (E).
  const haveLoadedKeyRef = useRef(new Set());

  const loadSessions = useCallback(async (silent = false) => {
    const filterKey = `${filter}::${chatType}`;
    const firstLoadForCombo = !haveLoadedKeyRef.current.has(filterKey);
    if (!silent) {
      if (firstLoadForCombo) setLoadingSessions(true);
      else setRefreshingSessions(true);
    }
    // Cancel any previous in-flight request — stops Active-tab data from
    // overwriting Mine-tab data when the user clicks faster than the
    // network responds.
    if (sessionsAbortRef.current) sessionsAbortRef.current.abort();
    const controller = new AbortController();
    sessionsAbortRef.current = controller;
    try {
      const params = [`limit=${PAGE_SIZE}`, `offset=0`];
      if (filter === 'mine') params.push('mine_only=true');
      if (filter === 'follow_ups') params.push('follow_ups_only=true');
      if (filter === 'active') params.push('active=true');
      if (filter === 'inactive') params.push('active=false');
      if (filter === 'unread') params.push('unread_only=true');
      if (filter === 'hot') params.push('hot_only=true');
      if (chatType !== 'all') params.push(`chat_type=${chatType}`);
      const r = await api.get(`/smb/chat/sessions?${params.join('&')}`, { signal: controller.signal });
      if (r.data.success) {
        setSessions(r.data.data);
        setSessionsTotal(r.data.total ?? r.data.data.length);
        setSessionsHasMore(!!r.data.has_more);
        haveLoadedKeyRef.current.add(filterKey);
      }
    } catch (err) {
      // AbortError is expected when the user switches tabs — don't toast.
      if (err?.name === 'CanceledError' || err?.name === 'AbortError'
          || err?.code === 'ERR_CANCELED') return;
      if (!silent) toast.error('Failed to load conversations');
    } finally {
      if (!silent) {
        setLoadingSessions(false);
        setRefreshingSessions(false);
      }
    }
  }, [filter, chatType]);

  const loadMoreSessions = useCallback(async () => {
    if (loadingMoreSessions || !sessionsHasMore) return;
    setLoadingMoreSessions(true);
    try {
      const params = [`limit=${PAGE_SIZE}`, `offset=${sessions.length}`];
      if (filter === 'mine') params.push('mine_only=true');
      if (filter === 'follow_ups') params.push('follow_ups_only=true');
      if (filter === 'active') params.push('active=true');
      if (filter === 'inactive') params.push('active=false');
      if (filter === 'unread') params.push('unread_only=true');
      if (filter === 'hot') params.push('hot_only=true');
      if (chatType !== 'all') params.push(`chat_type=${chatType}`);
      const r = await api.get(`/smb/chat/sessions?${params.join('&')}`);
      if (r.data.success) {
        // Dedupe by phone_e164 in case a poll-tick refreshed mid-scroll.
        const seen = new Set(sessions.map(s => s.phone_e164));
        const merged = [...sessions, ...r.data.data.filter(s => !seen.has(s.phone_e164))];
        setSessions(merged);
        setSessionsTotal(r.data.total ?? merged.length);
        setSessionsHasMore(!!r.data.has_more);
      }
    } catch {
      toast.error('Failed to load more conversations');
    } finally {
      setLoadingMoreSessions(false);
    }
  }, [filter, chatType, sessions, loadingMoreSessions, sessionsHasMore]);

  const loadFollowUpSummary = useCallback(async () => {
    try {
      const r = await api.get('/smb/chat/follow-ups/summary');
      if (r.data.success) setFollowUpSummary(r.data.data);
    } catch { /* silent — non-critical chip */ }
  }, []);

  const loadAssignableUsers = useCallback(async () => {
    try {
      const r = await api.get('/smb/chat/assignable-users');
      if (r.data.success) setAssignableUsers(r.data.data);
    } catch { /* silent — popover shows "Loading…" until success */ }
  }, []);

  const saveAssignment = async (userId, userName) => {
    if (!thread?.lead?.id) return;
    setAssignSaving(true);
    try {
      const r = await api.put(`/smb/chat/sessions/${thread.lead.id}/assignment`, { user_id: userId });
      if (r.data.success) {
        const lead = r.data.data;
        setThread(t => t ? { ...t, lead: {
          ...t.lead,
          assigned_to: lead.assigned_to,
          assigned_to_name: lead.assigned_to_name,
        } } : t);
        setAssignOpen(false);
        toast.success(userId ? `Assigned to ${userName}` : 'Chat unassigned');
        loadSessions(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssignSaving(false);
    }
  };

  const saveFollowUp = async () => {
    if (!thread?.lead?.id) return;
    setFollowUpSaving(true);
    try {
      const body = followUpAt
        ? { follow_up_at: new Date(followUpAt).toISOString(), follow_up_note: followUpNote }
        : { follow_up_at: null };
      const r = await api.put(`/smb/chat/sessions/${thread.lead.id}/follow-up`, body);
      if (r.data.success) {
        setThread(t => t ? { ...t, session: { ...t.session, ...r.data.data } } : t);
        setFollowUpOpen(false);
        toast.success(followUpAt ? 'Follow-up scheduled' : 'Follow-up cleared');
        loadSessions(true);
        loadFollowUpSummary();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save follow-up');
    } finally {
      setFollowUpSaving(false);
    }
  };

  const applyFollowUpPreset = (preset) => {
    const d = new Date();
    d.setSeconds(0, 0);
    if (preset === 'tomorrow') {
      d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0);
    } else if (preset === '2days') {
      d.setDate(d.getDate() + 2); d.setHours(10, 0, 0, 0);
    } else if (preset === 'next_monday') {
      const dow = d.getDay(); // 0=Sun
      const add = ((1 - dow + 7) % 7) || 7;
      d.setDate(d.getDate() + add); d.setHours(10, 0, 0, 0);
    } else if (preset === 'next_week') {
      d.setDate(d.getDate() + 7); d.setHours(10, 0, 0, 0);
    }
    // Format as datetime-local (YYYY-MM-DDTHH:MM in local time, no tz suffix)
    const pad = (n) => String(n).padStart(2, '0');
    setFollowUpAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  };

  const loadThread = useCallback(async (leadId, silent = false) => {
    if (!leadId) return;
    if (!silent) setLoadingThread(true);
    try {
      const r = await api.get(`/smb/chat/sessions/${leadId}`);
      if (r.data.success) setThread(r.data.data);
      // Mark read in background — and refresh the global notifier so the
      // sidebar badge / tab title / favicon dot clear immediately instead
      // of waiting for the next 15s poll.
      api.post(`/smb/chat/sessions/${leadId}/read`)
        .then(() => refreshInboxNotify && refreshInboxNotify())
        .catch(() => {});
    } catch {
      if (!silent) toast.error('Failed to load thread');
    } finally {
      if (!silent) setLoadingThread(false);
    }
  }, [refreshInboxNotify]);

  const handleDeleteConversation = async () => {    if (!thread?.lead?.id) return;    const phone = thread.lead.phone_e164 || thread.lead.phone || '';
    const ok = await confirm({
      title: 'Delete this conversation?',
      description: `All messages with ${phone || 'this lead'} will be permanently removed from the inbox. The lead itself stays. This cannot be undone.`,
      confirmText: 'Delete conversation',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      const r = await api.delete(`/smb/chat/sessions/${thread.lead.id}`);
      if (r.data.success) {
        const n = r.data.data?.messages_deleted || 0;
        toast.success(n ? `Deleted ${n} message${n === 1 ? '' : 's'}` : 'Conversation removed');
        setThread(null);
        setActiveId(null);
        loadSessions(true);
      } else {
        toast.error('Failed to delete conversation');
      }
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  const openConvertDialog = () => {
    if (!thread?.lead) return;
    setConvertForm({
      business_name: thread.lead.business_name || '',
      category: thread.lead.category || '',
      contact_name: thread.lead.contact_name || '',
    });
    setConvertOpen(true);
  };

  const handleConvertToSmb = async () => {
    if (!thread?.lead?.id) return;
    if (!convertForm.business_name.trim()) { toast.error('Business name is required'); return; }
    setConverting(true);
    try {
      const r = await api.post(`/smb/chat/sessions/${thread.lead.id}/convert-to-smb`, convertForm);
      if (r.data.success) {
        toast.success('Promoted to SMB Lead');
        setConvertOpen(false);
        loadSessions(true);
        loadThread(thread.lead.id, true);
      } else {
        toast.error(r.data.detail || 'Failed to convert');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  // Sessions reload on every filter / chat-type change. The list keeps
  // showing the previous data while the new tab's data fetches (see
  // refreshingSessions handling), so users see no flash of empty content.
  useEffect(() => { loadSessions(); }, [loadSessions]);
  // Follow-up summary is needed only for the "(N)" badge on the Follow-ups
  // pill — it changes when a follow-up is created/cleared, NOT when the
  // user switches between All/Active/Inactive tabs. Loading it only on
  // mount cuts one HTTP request per tab switch (F).
  useEffect(() => { loadFollowUpSummary(); }, [loadFollowUpSummary]);

  // Infinite-scroll: auto-load the next page when the sentinel scrolls into
  // view. Replaces the old 500-row hard cap that hid chats beyond #500.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !sessionsHasMore || search) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreSessions();
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [sessionsHasMore, search, loadMoreSessions, sessions.length]);

  useEffect(() => {
    api.get('/smb/outreach/templates').then(r => {
      if (r.data.success) setTemplates(r.data.data.filter(t => t.active));
    }).catch(() => {});
    // New independent gallery items (user-named, up to 20).
    api.get('/smb/outreach/gallery').then(r => {
      if (r.data.success) setGalleryItems(r.data.data || []);
    }).catch(() => {});
  }, []);

  // Auto-select first session
  useEffect(() => {
    if (!activeId && sessions.length > 0) {
      setActiveId(sessions[0].lead_id);
    }
  }, [sessions, activeId]);

  // Honor ?lead={id} from the inbox toast notification so users land
  // directly on the conversation that just pinged them.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leadParam = params.get('lead');
    if (leadParam && leadParam !== activeId) {
      setActiveId(leadParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (activeId) loadThread(activeId);
    setReplyTo(null);
    setPendingMessages([]);
    setPendingFile(prev => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, [activeId, loadThread]);

  // Polling for new messages — dynamic interval based on visibility + thread activity
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const computeMs = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return POLL_HIDDEN_MS;
      return thread?.session?.active ? POLL_ACTIVE_MS : POLL_INACTIVE_MS;
    };
    let currentMs = computeMs();
    const tick = () => {
      loadSessions(true);
      loadFollowUpSummary();
      if (activeId) loadThread(activeId, true);
    };
    pollRef.current = setInterval(tick, currentMs);
    const onVis = () => {
      // Reset interval on visibility change; if becoming visible, also fire immediately.
      if (pollRef.current) clearInterval(pollRef.current);
      currentMs = computeMs();
      if (document.visibilityState === 'visible') tick();
      pollRef.current = setInterval(tick, currentMs);
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [activeId, thread?.session?.active, loadSessions, loadThread]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [thread?.messages?.length, pendingMessages.length]);

  const isActive = thread?.session?.active;

  // Active/Inactive filtering is now applied server-side (see loadSessions);
  // here we only do the per-keystroke search narrowing.
  const filteredSessions = sessions.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      return (s.business_name || '').toLowerCase().includes(q) ||
             (s.contact_name || '').toLowerCase().includes(q) ||
             (s.phone || '').includes(search);
    }
    return true;
  });

  const handleSend = async () => {
    if (!activeId) return;
    // Media branch — file attached
    if (pendingFile && isActive) {
      return handleSendMedia();
    }
    if (isActive) {
      if (!text.trim()) { toast.error('Type a message first'); return; }
    } else {
      if (!tplKey) { toast.error('Pick a template'); return; }
    }
    setSending(true);

    // Optimistic outbound — show the bubble immediately with status="sending"
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = isActive ? {
      id: tempId,
      tempId,
      direction: 'outbound',
      kind: 'text',
      body: text.trim(),
      status: 'sending',
      created_at: new Date().toISOString(),
      reply_to_wa_id: replyTo?.wa_message_id || null,
    } : null;
    const sentText = text.trim();
    const sentReplyTo = replyTo;
    if (optimistic) {
      setPendingMessages(prev => [...prev, optimistic]);
      setText('');
      setReplyTo(null);
    }

    try {
      const payload = isActive
        ? { kind: 'text', body: sentText }
        : { kind: 'template', template_key: tplKey };
      if (sentReplyTo?.wa_message_id) {
        payload.reply_to_message_id = sentReplyTo.wa_message_id;
      }
      const r = await api.post(`/smb/chat/send/${activeId}`, payload);
      if (r.data.success) {
        if (!isActive) setTplKey('');
        // Drop the optimistic placeholder — the next poll/refresh will bring the real msg
        setPendingMessages(prev => prev.filter(m => m.tempId !== tempId));
        loadThread(activeId, true);
        loadSessions(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
      // Mark the optimistic message as failed so the user sees it didn't go through
      setPendingMessages(prev => prev.map(m => m.tempId === tempId ? { ...m, status: 'failed' } : m));
    } finally {
      setSending(false);
    }
  };

  const handleSendMedia = async () => {
    if (!pendingFile || !activeId) return;
    setSending(true);
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const isImage = pendingFile.file.type.startsWith('image/');
    const optimistic = {
      id: tempId,
      tempId,
      direction: 'outbound',
      kind: isImage ? 'image' : 'document',
      body: text.trim() || (isImage ? '📷 Image' : `📄 ${pendingFile.file.name}`),
      caption: text.trim() || null,
      media_url: pendingFile.previewUrl, // local blob URL renders instantly
      media_mime: pendingFile.file.type,
      media_filename: pendingFile.file.name,
      status: 'sending',
      created_at: new Date().toISOString(),
      reply_to_wa_id: replyTo?.wa_message_id || null,
    };
    setPendingMessages(prev => [...prev, optimistic]);
    const fileToSend = pendingFile.file;
    const cap = text.trim();
    const replyId = replyTo?.wa_message_id || '';
    setText('');
    setPendingFile(null);
    setReplyTo(null);

    try {
      const fd = new FormData();
      fd.append('file', fileToSend);
      fd.append('caption', cap);
      fd.append('reply_to_message_id', replyId);
      const r = await api.post(`/smb/chat/send-media/${activeId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data.success) {
        const realMsg = r.data.data;
        const realMediaUrl = realMsg?.media_url;
        const finalize = () => {
          setPendingMessages(prev => prev.filter(m => m.tempId !== tempId));
          loadThread(activeId, true);
          loadSessions(true);
        };
        // Preload the backend image so the bubble swap is flicker-free.
        // Without this, the optimistic blob bubble is removed and the new
        // bubble's <img> momentarily shows a broken-image icon while the
        // backend URL is still downloading.
        if (realMediaUrl && (realMsg.kind === 'image' || realMsg.kind === 'sticker')) {
          const fullUrl = /^https?:\/\//i.test(realMediaUrl)
            ? realMediaUrl
            : `${process.env.REACT_APP_BACKEND_URL || ''}${realMediaUrl}`;
          const pre = new Image();
          let done = false;
          const onDone = () => { if (!done) { done = true; finalize(); } };
          pre.onload = onDone;
          pre.onerror = onDone;
          pre.src = fullUrl;
          // Hard fallback so we never get stuck if the preload hangs.
          setTimeout(onDone, 4000);
        } else {
          finalize();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send media');
      setPendingMessages(prev => prev.map(m => m.tempId === tempId ? { ...m, status: 'failed' } : m));
    } finally {
      setSending(false);
    }
  };

  const onEmojiSelect = (emojiData) => {
    const emoji = emojiData?.emoji || '';
    if (!emoji) return;
    const el = textareaRef.current;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = text.slice(0, start) + emoji + text.slice(end);
      setText(next);
      // restore cursor after the inserted emoji
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + emoji.length;
        try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
      });
    } else {
      setText(text + emoji);
    }
    setEmojiOpen(false);
  };

  const acceptFile = (file) => {
    if (!file) return;
    const okImage = file.type.startsWith('image/');
    const okDoc = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain', 'text/csv'].includes(file.type);
    if (!okImage && !okDoc) { toast.error(`Unsupported file type: ${file.type || 'unknown'}`); return; }
    const cap = okImage ? 5 * 1024 * 1024 : 100 * 1024 * 1024;
    if (file.size > cap) { toast.error(`File too large (max ${okImage ? '5 MB' : '100 MB'})`); return; }
    const previewUrl = okImage ? URL.createObjectURL(file) : null;
    setPendingFile({ file, previewUrl });
  };

  const handleFilePicked = (e) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = ''; // allow re-selecting the same file
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          acceptFile(f);
          return;
        }
      }
    }
  };

  const cancelPendingFile = () => {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
  };

  // Pick a gallery item — downloads the pre-uploaded image and pre-fills
  // the composer (image + caption). Agent can edit the caption before sending.
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryLoadingKey, setGalleryLoadingKey] = useState(null);
  const galleryTemplates = galleryItems.filter(t => t.image_url);

  const pickGalleryTemplate = async (tpl) => {
    if (!tpl?.image_url) return;
    setGalleryLoadingKey(tpl.id);
    try {
      const fullUrl = /^https?:\/\//i.test(tpl.image_url)
        ? tpl.image_url
        : `${process.env.REACT_APP_BACKEND_URL || ''}${tpl.image_url}`;
      const res = await fetch(fullUrl);
      if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
      const blob = await res.blob();
      const filename = tpl.image_filename || `template-${tpl.id}.jpg`;
      const fileObj = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      // Replace any existing pending file (revoke previous blob)
      setPendingFile(prev => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return { file: fileObj, previewUrl: URL.createObjectURL(fileObj) };
      });
      // Pre-fill caption from template (overwrite any existing text — that's the expected flow)
      if (tpl.caption) setText(tpl.caption);
      setGalleryOpen(false);
    } catch (err) {
      toast.error(`Failed to load template image: ${err.message}`);
    } finally {
      setGalleryLoadingKey(null);
    }
  };

  // Build the gallery list for the lightbox — only image messages, in chronological order.
  const imageMessages = React.useMemo(() => {
    return (thread?.messages || [])
      .filter(m => (m.kind === 'image' || m.kind === 'sticker') && m.media_url)
      .map(m => ({
        id: m.id,
        media_url: m.media_url,
        media_filename: m.media_filename,
        caption: m.caption,
        created_at: m.created_at,
        direction: m.direction,
        wa_message_id: m.wa_message_id,
        msg: m,
      }));
  }, [thread?.messages]);

  const openLightboxForMessage = (msg) => {
    const idx = imageMessages.findIndex(im => im.id === msg.id);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };

  return (
    <div className="animate-fade-in" data-testid="smb-inbox-page">
      <SmbOutreachAutomationBanner />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><InboxIcon className="h-6 w-6 text-primary" />WhatsApp Inbox</h1>
          <p className="text-muted-foreground text-sm">Live conversations with SMB leads.</p>
        </div>
        <div className="flex items-center gap-2">
          {followUpSummary.total_pending > 0 && (
            <button
              type="button"
              onClick={() => setFilter('follow_ups')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted hover:bg-muted/70 text-xs"
              title="Click to filter the inbox to chats with follow-ups"
              data-testid="follow-up-summary-chip"
            >
              <Clock className="h-3.5 w-3.5 text-amber-500" />
              {followUpSummary.overdue > 0 && (
                <span className="text-red-500 font-semibold">{followUpSummary.overdue} overdue</span>
              )}
              {followUpSummary.due_today > 0 && (
                <span className="text-amber-500 font-semibold">{followUpSummary.overdue > 0 ? '· ' : ''}{followUpSummary.due_today} today</span>
              )}
              {followUpSummary.overdue === 0 && followUpSummary.due_today === 0 && (
                <span className="text-muted-foreground">{followUpSummary.total_pending} pending</span>
              )}
            </button>
          )}
          <Button variant="outline" size="sm" onClick={() => loadSessions()} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-12rem)]">
        {/* LEFT PANEL */}
        <Card className="col-span-12 md:col-span-4 lg:col-span-3 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search business, name, phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="inbox-search" />
            </div>
            <div className="flex gap-1 text-xs" data-testid="inbox-chat-type-pills">
              {[
                { v: 'all', label: 'All Chats', visible: canSeeBoth },
                { v: 'smb', label: 'SMB', visible: canSeeSmb },
                { v: 'general', label: 'General', visible: canSeeGeneral },
              ].filter(o => o.visible).map(o => (
                <button
                  key={o.v}
                  onClick={() => setChatType(o.v)}
                  className={`px-2.5 py-1 rounded-md ${chatType === o.v ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}
                  data-testid={`chat-type-${o.v}`}
                >
                  {o.label}
                  {chatType === o.v && !loadingSessions && (
                    <span className="ml-1 opacity-80 tabular-nums" data-testid={`chat-type-${o.v}-count`}>
                      ({search ? filteredSessions.length : sessionsTotal})
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 text-xs flex-wrap" data-testid="inbox-filter-row">
              {/* "Scope" filters narrow which chats are listed. */}
              {['all', 'active', 'inactive', 'mine', 'follow_ups'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-md capitalize ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'} ${f === 'follow_ups' && followUpSummary.total_pending > 0 ? 'relative' : ''}`}
                  data-testid={`filter-${f}`}
                >
                  {f === 'mine' ? 'Mine' : f === 'follow_ups' ? 'Follow-ups' : f}
                  {filter === f && !loadingSessions && (
                    <span className="ml-1 opacity-80 tabular-nums" data-testid={`filter-${f}-count`}>
                      ({search ? filteredSessions.length : sessionsTotal})
                    </span>
                  )}
                  {filter !== f && f === 'follow_ups' && followUpSummary.total_pending > 0 && (
                    <span className="ml-1 text-[10px] font-semibold">{followUpSummary.total_pending}</span>
                  )}
                </button>
              ))}
              {/* Divider — visually separates "scope" pills from
                  "attention" pills (Unread / Hot) so the intent is
                  obvious: scope = "which side of the inbox am I in",
                  attention = "which chats need me to act now". */}
              <span
                aria-hidden
                className="mx-1 h-4 w-px bg-border self-center"
                data-testid="filter-group-divider"
              />
              {/* Unread — count is always visible (cheap signal,
                  actionable). Hot count only appears when the pill
                  is active because the temperature classifier needs
                  a full inbox scan to count accurately. */}
              <button
                onClick={() => setFilter('unread')}
                className={`px-2.5 py-1 rounded-md ${filter === 'unread' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}
                data-testid="filter-unread"
              >
                Unread
                {filter === 'unread' && !loadingSessions ? (
                  <span className="ml-1 opacity-80 tabular-nums" data-testid="filter-unread-count">
                    ({search ? filteredSessions.length : sessionsTotal})
                  </span>
                ) : globalUnreadLeadCount > 0 ? (
                  <span
                    className="ml-1 text-[10px] font-semibold tabular-nums"
                    data-testid="filter-unread-badge"
                  >
                    {globalUnreadLeadCount}
                  </span>
                ) : null}
              </button>
              <button
                onClick={() => setFilter('hot')}
                className={`px-2.5 py-1 rounded-md ${filter === 'hot' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}
                data-testid="filter-hot"
                title="Chats classified as 🔥 Hot by the temperature classifier"
              >
                <span aria-hidden className="mr-0.5">🔥</span>Hot
                {filter === 'hot' && !loadingSessions && (
                  <span className="ml-1 opacity-80 tabular-nums" data-testid="filter-hot-count">
                    ({search ? filteredSessions.length : sessionsTotal})
                  </span>
                )}
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto scroll-shadows relative">
            {refreshingSessions && (
              <div
                className="absolute top-0 left-0 right-0 h-0.5 bg-primary/30 overflow-hidden z-10"
                data-testid="sessions-refreshing"
              >
                <div className="h-full w-1/3 bg-primary animate-[slide-x_1.2s_ease-in-out_infinite]" />
              </div>
            )}
            {loadingSessions ? (
              <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : filteredSessions.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <InboxIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                No conversations yet.
              </div>
            ) : (
              filteredSessions.map(s => (
                <button
                  key={s.lead_id}
                  onClick={() => setActiveId(s.lead_id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted/30 transition-colors ${activeId === s.lead_id ? 'bg-muted/40' : ''}`}
                  data-testid={`session-${s.lead_id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${s.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                      {(s.business_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-semibold text-sm truncate">{s.business_name || 'Unknown'}</p>
                        <TempBadge t={s.temperature} />
                        {s.last_handled_by_name && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] py-0 px-1.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/30"
                            title={`Last replied by ${s.last_handled_by_name}${s.last_handled_at ? ' · ' + formatDateTime(s.last_handled_at) : ''}`}
                            data-testid="handled-by-pill"
                          >
                            <User className="h-2.5 w-2.5" />
                            {initialsOf(s.last_handled_by_name)}
                          </span>
                        )}
                        {s.chat_type === 'general' && (
                          <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-amber-500/40 text-amber-500" title="Inbound chat — not linked to any SMB Lead">
                            General
                          </Badge>
                        )}
                        {s.linked_lead_count > 1 && (
                          <Badge variant="outline" className="text-[9px] py-0 px-1.5" title={`${s.linked_lead_count} leads share this number`}>
                            {s.linked_lead_count} leads
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{s.last_message_preview || '—'}</p>
                      <div className="flex items-center justify-between mt-1">
                        <Badge className={`text-[9px] py-0 px-1.5 ${s.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                          {s.active ? 'Live Chat' : 'Template Only'}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{s.last_message_at ? formatDateTime(s.last_message_at).split(' ').slice(0, 2).join(' ') : ''}</span>
                      </div>
                      {s.unread_count > 0 && (
                        <Badge className="mt-1 bg-red-500 text-white text-[10px] py-0 px-1.5">{s.unread_count} unread</Badge>
                      )}
                      {s.follow_up_bucket && (
                        <Badge
                          className={`mt-1 ml-1 text-[10px] py-0 px-1.5 gap-1 ${
                            s.follow_up_bucket === 'overdue' ? 'bg-red-500/15 text-red-400 border border-red-500/40'
                            : s.follow_up_bucket === 'today' ? 'bg-amber-500/15 text-amber-500 border border-amber-500/40'
                            : 'bg-muted text-muted-foreground'
                          }`}
                          title={s.follow_up_note ? `Follow-up: ${s.follow_up_note}` : 'Follow-up scheduled'}
                          data-testid={`follow-up-row-chip-${s.lead_id}`}
                        >
                          <Clock className="h-2.5 w-2.5" />
                          {s.follow_up_bucket === 'overdue' ? 'Overdue'
                            : s.follow_up_bucket === 'today' ? 'Due today'
                            : s.follow_up_bucket === 'this_week' ? formatDateTime(s.follow_up_at).split(' ').slice(0, 2).join(' ')
                            : formatDateTime(s.follow_up_at).split(' ').slice(0, 2).join(' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
            {sessionsHasMore && !search && (
              <div ref={loadMoreRef} className="p-3 text-center text-xs text-muted-foreground" data-testid="load-more-sentinel">
                {loadingMoreSessions ? (
                  <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />Loading more…</span>
                ) : (
                  <button onClick={loadMoreSessions} className="hover:text-foreground" data-testid="load-more-btn">
                    Showing {sessions.length} of {sessionsTotal} · Load more
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* RIGHT PANEL */}
        <Card className="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col overflow-hidden">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              {activeId ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Select a conversation to start chatting</p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                <div>
                  <p className="font-semibold flex items-center gap-2">
                    {thread.lead.business_name}
                    <TempBadge t={thread.temperature} />
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {thread.lead.contact_name || ''} · {thread.lead.phone_e164 || thread.lead.phone}
                  </p>
                  {thread.session?.last_handled_by_name && (
                    <p className="text-[11px] text-blue-400 mt-0.5 flex items-center gap-1" data-testid="handled-by-line">
                      <User className="h-3 w-3" />
                      Last replied by <span className="font-medium">{thread.session.last_handled_by_name}</span>
                      {thread.session.last_handled_at && (
                        <span className="text-muted-foreground">· {formatDateTime(thread.session.last_handled_at)}</span>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={isActive ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-muted text-muted-foreground'}>
                    {isActive ? <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live Chat (24h)</span> : <><Lock className="h-3 w-3 mr-1 inline" />Template Only</>}
                  </Badge>
                  <Popover open={assignOpen} onOpenChange={(o) => {
                    setAssignOpen(o);
                    if (o && !assignableUsers.length) loadAssignableUsers();
                  }}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border transition-colors ${
                          thread.lead?.assigned_to_name
                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20'
                            : 'bg-muted text-muted-foreground border-border hover:bg-muted/70'
                        }`}
                        title={thread.lead?.assigned_to_name
                          ? `Assigned to ${thread.lead.assigned_to_name}. Click to reassign.`
                          : 'Unassigned — click to claim or assign.'}
                        data-testid="assigned-to-chip"
                      >
                        <User className="h-3 w-3" />
                        {thread.lead?.assigned_to_name
                          ? thread.lead.assigned_to_name.split(' ')[0]
                          : 'Unassigned'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="end" data-testid="assignment-popover">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground px-2 pb-1 border-b border-border">
                          Assign chat
                        </p>
                        {!assignableUsers.length ? (
                          <p className="text-xs text-muted-foreground p-3 text-center">Loading…</p>
                        ) : (
                          <>
                            {assignableUsers.map(u => {
                              const isCurrent = u.id === thread.lead?.assigned_to;
                              return (
                                <button
                                  key={u.id}
                                  type="button"
                                  onClick={() => saveAssignment(u.id, u.name)}
                                  disabled={isCurrent || assignSaving}
                                  className={`w-full text-left px-2 py-1.5 rounded-md text-xs flex items-center justify-between ${isCurrent ? 'bg-blue-500/10 text-blue-400 cursor-default' : 'hover:bg-muted'}`}
                                  data-testid={`assign-to-${u.id}`}
                                >
                                  <span className="flex items-center gap-1.5">
                                    <User className="h-3 w-3" />
                                    {u.name}
                                    {u.is_me && <span className="text-muted-foreground">(me)</span>}
                                  </span>
                                  {isCurrent && <Check className="h-3 w-3" />}
                                </button>
                              );
                            })}
                            {thread.lead?.assigned_to && (
                              <button
                                type="button"
                                onClick={() => saveAssignment(null)}
                                disabled={assignSaving}
                                className="w-full text-left px-2 py-1.5 rounded-md text-xs hover:bg-red-500/10 text-red-400 border-t border-border mt-1 pt-1.5"
                                data-testid="assign-clear-btn"
                              >
                                <span className="flex items-center gap-1.5">
                                  <X className="h-3 w-3" />Unassign
                                </span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover open={followUpOpen} onOpenChange={(o) => {
                    setFollowUpOpen(o);
                    if (o) {
                      // Pre-fill from current session value (format for datetime-local input)
                      const curr = thread.session?.follow_up_at;
                      if (curr) {
                        const d = new Date(curr);
                        const pad = (n) => String(n).padStart(2, '0');
                        setFollowUpAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                      } else {
                        setFollowUpAt('');
                      }
                      setFollowUpNote(thread.session?.follow_up_note || '');
                    }
                  }}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost" size="sm"
                        className={thread.session?.follow_up_at ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-500/10' : ''}
                        title={thread.session?.follow_up_at ? `Follow-up: ${formatDateTime(thread.session.follow_up_at)}${thread.session.follow_up_note ? ' · ' + thread.session.follow_up_note : ''}` : 'Schedule a follow-up reminder for this chat'}
                        data-testid="follow-up-btn"
                      >
                        <CalendarClock className="h-3.5 w-3.5 mr-1" />
                        {thread.session?.follow_up_at ? formatDateTime(thread.session.follow_up_at).split(' ').slice(0, 2).join(' ') : 'Follow-up'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="end" data-testid="follow-up-popover">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-semibold">Schedule follow-up</p>
                          <p className="text-xs text-muted-foreground">This chat will surface in the &ldquo;Follow-ups&rdquo; filter at the chosen time.</p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            { v: 'tomorrow', label: 'Tomorrow 10am' },
                            { v: '2days', label: 'In 2 days' },
                            { v: 'next_monday', label: 'Next Monday' },
                            { v: 'next_week', label: 'In 1 week' },
                          ].map(p => (
                            <button
                              key={p.v}
                              type="button"
                              onClick={() => applyFollowUpPreset(p.v)}
                              className="px-2.5 py-1 rounded-md bg-muted hover:bg-muted/70 text-xs"
                              data-testid={`follow-up-preset-${p.v}`}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fu-at" className="text-xs">Date &amp; time</Label>
                          <Input
                            id="fu-at"
                            type="datetime-local"
                            value={followUpAt}
                            onChange={e => setFollowUpAt(e.target.value)}
                            data-testid="follow-up-datetime"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="fu-note" className="text-xs">Note (optional)</Label>
                          <Input
                            id="fu-note"
                            placeholder="e.g. send revised mockup"
                            maxLength={200}
                            value={followUpNote}
                            onChange={e => setFollowUpNote(e.target.value)}
                            data-testid="follow-up-note"
                          />
                        </div>
                        <div className="flex items-center justify-between gap-2 pt-1">
                          {thread.session?.follow_up_at ? (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => { setFollowUpAt(''); setFollowUpNote(''); saveFollowUp(); }}
                              disabled={followUpSaving}
                              className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              data-testid="follow-up-clear-btn"
                            >
                              Clear
                            </Button>
                          ) : <span />}
                          <Button
                            size="sm"
                            onClick={saveFollowUp}
                            disabled={!followUpAt || followUpSaving}
                            data-testid="follow-up-save-btn"
                          >
                            {followUpSaving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                            Save
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {thread.lead.is_general ? (
                    canSeeSmb && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openConvertDialog}
                        className="border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
                        data-testid="convert-to-smb-btn"
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1" />Convert to SMB Lead
                      </Button>
                    )
                  ) : (
                    <Button asChild variant="ghost" size="sm" data-testid="open-lead-btn">
                      <a
                        href={`/smb/leads/${thread.lead.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open lead in a new tab (right-click for more options)"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />Lead
                      </a>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteConversation}
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    title="Delete this conversation"
                    data-testid="delete-conversation-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div ref={messagesEndRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-muted/10">
                {loadingThread && thread.messages.length === 0 ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                ) : (thread.messages.length === 0 && pendingMessages.length === 0) ? (
                  <p className="text-center text-sm text-muted-foreground py-12">No messages yet.</p>
                ) : (() => {
                  // Merge optimistic outbound (pending) into the timeline.
                  const merged = [...thread.messages, ...pendingMessages];
                  // Build a wa_id → message lookup so we can render quoted-reply previews
                  // and group reactions under the message they target.
                  const byWaId = {};
                  merged.forEach(m => { if (m.wa_message_id) byWaId[m.wa_message_id] = m; });
                  const reactionsByTarget = {};
                  merged.forEach(m => {
                    if (m.kind === 'reaction' && m.reaction_to_wa_id) {
                      (reactionsByTarget[m.reaction_to_wa_id] = reactionsByTarget[m.reaction_to_wa_id] || []).push(m);
                    }
                  });
                  return merged
                    .filter(m => m.kind !== 'reaction') // reactions render as badges on the target bubble
                    .map(m => {
                      const out = m.direction === 'outbound';
                      const isMedia = ['image', 'audio', 'voice', 'video', 'document', 'sticker'].includes(m.kind);
                      const quoted = m.reply_to_wa_id ? byWaId[m.reply_to_wa_id] : null;
                      const reactions = (reactionsByTarget[m.wa_message_id] || [])
                        .filter(r => r.reaction_emoji); // ignore "reaction removed" markers
                      const failed = m.status === 'failed';
                      const sending = m.status === 'sending';
                      return (
                        <div key={m.id} className={`group flex ${out ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                          <div className="flex items-center gap-1 max-w-[75%]">
                            {/* Reply button — show on hover, on the LEFT of outbound bubbles and RIGHT of inbound */}
                            {out && m.wa_message_id && !sending && !failed && (
                              <button
                                onClick={() => setReplyTo(m)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-muted text-muted-foreground"
                                title="Reply to this message"
                                data-testid={`reply-btn-${m.id}`}
                              >
                                <CornerUpLeft className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <div className={`relative rounded-2xl px-3 py-2 ${out ? 'bg-[#d9fdd3] text-gray-900' : 'bg-background border border-border'} ${m.kind === 'template' ? 'border-l-4 border-l-amber-500' : ''} ${!out && m.is_auto_reply ? 'border-l-4 border-l-slate-400' : ''} ${sending ? 'opacity-70' : ''} ${failed ? 'ring-1 ring-red-500/50' : ''}`}>
                              {m.kind === 'template' && (
                                <p className="text-[10px] uppercase opacity-70 mb-1 flex items-center gap-1"><FileText className="h-3 w-3" />Template{m.template_name ? `: ${m.template_name}` : ''}</p>
                              )}
                              {!out && m.is_auto_reply && (
                                <p className="text-[10px] uppercase opacity-70 mb-1 flex items-center gap-1" data-testid={`auto-reply-chip-${m.id}`} title={`Auto-reply detected (${m.auto_reply_reason || 'heuristic'}) — outreach automation continues`}>
                                  <CornerUpLeft className="h-3 w-3" />Auto-reply · automation continues
                                </p>
                              )}
                              {quoted && (
                                <div className={`mb-1.5 px-2 py-1 rounded border-l-2 text-xs opacity-90 ${out ? 'bg-black/5 border-emerald-600/60' : 'bg-muted/40 border-primary/60'}`} data-testid={`quoted-${m.id}`}>
                                  <p className="font-semibold opacity-80 mb-0.5">{quoted.direction === 'outbound' ? 'You' : (thread.lead.contact_name || thread.lead.business_name || 'Them')}</p>
                                  <p className="truncate max-w-[260px]">{quoted.kind === 'text' ? quoted.body : (quoted.caption || quoted.body || quoted.kind)}</p>
                                </div>
                              )}
                              {isMedia ? (
                                <>
                                  <p className="text-[10px] uppercase opacity-70 mb-1 flex items-center gap-1"><KindIcon kind={m.kind} />{m.kind}</p>
                                  <div className="mb-1"><MediaAttachment msg={m} onImageClick={openLightboxForMessage} /></div>
                                  {m.caption && (
                                    <p className="text-sm whitespace-pre-wrap break-words">
                                      <LinkifiedText text={m.caption} />
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  <LinkifiedText text={m.body} />
                                </p>
                              )}
                              <div className="flex items-center gap-1 mt-1 text-[10px] opacity-70">
                                <span>{formatDateTime(m.created_at)}</span>
                                {out && sending && <Loader2 className="h-3 w-3 animate-spin" />}
                                {out && failed && <span className="text-red-600 flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />Failed</span>}
                                {out && !sending && !failed && <StatusIcon status={m.status} />}
                                {m.stub && <span className="italic">(stub)</span>}
                              </div>
                              {reactions.length > 0 && (
                                <div className={`absolute -bottom-2 ${out ? 'left-2' : 'right-2'} flex gap-0.5 px-1.5 py-0.5 rounded-full bg-background border border-border shadow-sm text-sm leading-none`} data-testid={`reactions-${m.id}`}>
                                  {reactions.map(r => <span key={r.id} title="Reaction">{r.reaction_emoji}</span>)}
                                </div>
                              )}
                            </div>
                            {!out && m.wa_message_id && (
                              <button
                                onClick={() => setReplyTo(m)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-muted text-muted-foreground"
                                title="Reply to this message"
                                data-testid={`reply-btn-${m.id}`}
                              >
                                <CornerUpLeft className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    });
                })()}
              </div>

              {/* Composer */}
              <div className="border-t border-border p-3">
                {replyTo && (
                  <div className="mb-2 flex items-start gap-2 px-2.5 py-1.5 rounded-md bg-muted/50 border-l-2 border-primary text-xs" data-testid="reply-preview">
                    <CornerUpLeft className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-70" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold opacity-80">
                        Replying to {replyTo.direction === 'outbound' ? 'yourself' : (thread.lead.contact_name || thread.lead.business_name || 'them')}
                      </p>
                      <p className="truncate opacity-90">
                        {replyTo.kind === 'text' ? replyTo.body : (replyTo.caption || replyTo.body || replyTo.kind)}
                      </p>
                    </div>
                    <button onClick={() => setReplyTo(null)} className="p-0.5 rounded hover:bg-muted" data-testid="reply-cancel-btn" title="Cancel reply">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {pendingFile && (
                  <div className="mb-2 flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-muted/50 border border-border text-xs" data-testid="pending-file-preview">
                    {pendingFile.previewUrl ? (
                      <img src={pendingFile.previewUrl} alt="preview" className="h-10 w-10 object-cover rounded" />
                    ) : (
                      <Paperclip className="h-4 w-4 opacity-70" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{pendingFile.file.name}</p>
                      <p className="opacity-60 text-[10px]">{(pendingFile.file.size / 1024).toFixed(0)} KB · {pendingFile.file.type || 'file'}</p>
                    </div>
                    <button onClick={cancelPendingFile} className="p-0.5 rounded hover:bg-muted" data-testid="pending-file-cancel-btn" title="Remove attachment">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {isActive ? (
                  <div className="flex items-end gap-2">
                    {/* Attach file */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
                      onChange={handleFilePicked}
                      data-testid="file-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending}
                      title="Attach file"
                      data-testid="attach-file-btn"
                    >
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    {/* Emoji */}
                    <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" disabled={sending} title="Insert emoji" data-testid="emoji-toggle-btn">
                          <Smile className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-auto border-none bg-transparent" side="top" align="start" data-testid="emoji-popover">
                        <Suspense fallback={<div className="p-4 text-xs">Loading…</div>}>
                          <EmojiPicker onEmojiClick={onEmojiSelect} theme="dark" lazyLoadEmojis searchPlaceholder="Search emoji..." width={320} height={400} />
                        </Suspense>
                      </PopoverContent>
                    </Popover>
                    {/* Templates gallery */}
                    <Popover open={galleryOpen} onOpenChange={setGalleryOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" disabled={sending} title="Templates" data-testid="templates-toggle-btn">
                          <LayoutGrid className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-2" side="top" align="start" data-testid="templates-popover">
                        <p className="text-xs font-semibold uppercase tracking-wide opacity-70 px-1 pb-1.5">Templates</p>
                        {galleryTemplates.length === 0 ? (
                          <div className="px-1 py-6 text-center text-xs text-muted-foreground">
                            <p>No gallery templates yet.</p>
                            <p className="mt-1 opacity-70">Go to <span className="font-mono">Outreach Automation → Inbox Gallery</span> to add one.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                            {galleryTemplates.map(t => {
                              const thumb = /^https?:\/\//i.test(t.image_url)
                                ? t.image_url
                                : `${process.env.REACT_APP_BACKEND_URL || ''}${t.image_url}`;
                              const isLoading = galleryLoadingKey === t.id;
                              return (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => pickGalleryTemplate(t)}
                                  disabled={isLoading}
                                  className="text-left rounded-md border border-border hover:border-primary/60 overflow-hidden transition-colors disabled:opacity-60"
                                  data-testid={`gallery-tpl-${t.id}`}
                                >
                                  <img src={thumb} alt={t.name || 'template'} className="w-full h-20 object-cover bg-muted" />
                                  <div className="p-1.5">
                                    <p className="text-xs font-semibold truncate" title={t.name}>{t.name || 'Untitled'}</p>
                                    {t.caption && (
                                      <p className="text-[10px] opacity-70 line-clamp-2">{t.caption}</p>
                                    )}
                                    {isLoading && <p className="text-[10px] mt-0.5 flex items-center gap-1"><Loader2 className="h-2.5 w-2.5 animate-spin" />Loading…</p>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                    <Textarea
                      ref={textareaRef}
                      rows={2}
                      value={text}
                      onChange={e => setText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                      onPaste={handlePaste}
                      placeholder={pendingFile ? 'Add a caption (optional)...' : 'Type your reply...'}
                      className="resize-none flex-1"
                      data-testid="msg-input"
                    />
                    <Button onClick={handleSend} disabled={sending || (!text.trim() && !pendingFile)} data-testid="send-btn">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[11px] text-amber-500">
                      <Lock className="h-3.5 w-3.5" />
                      24-hour conversation window expired. Send an approved template to re-open the chat.
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Select value={tplKey} onValueChange={setTplKey}>
                          <SelectTrigger data-testid="tpl-select"><SelectValue placeholder="Select a template..." /></SelectTrigger>
                          <SelectContent>
                            {templates.map(t => (
                              <SelectItem key={t.key} value={t.key}>
                                {t.key.toUpperCase()} — {t.whatsapp_template_name || '(no WA name set)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleSend} disabled={sending || !tplKey} data-testid="send-tpl-btn">
                        {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                        Send Template
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
      <ChatLightbox
        open={lightboxOpen}
        images={imageMessages}
        index={lightboxIndex}
        onChangeIndex={setLightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onReply={setReplyTo}
      />

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent data-testid="convert-to-smb-dialog">
          <DialogHeader>
            <DialogTitle>Convert to SMB Lead</DialogTitle>
            <DialogDescription>
              Promote this General chat into a real SMB Lead. All historical
              messages stay linked to the same phone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Business Name *</Label>
              <Input
                value={convertForm.business_name}
                onChange={e => setConvertForm(f => ({ ...f, business_name: e.target.value }))}
                placeholder="e.g. Acme Catering"
                data-testid="convert-business-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Category <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={convertForm.category}
                onChange={e => setConvertForm(f => ({ ...f, category: e.target.value }))}
                placeholder="e.g. Food & Beverages"
                data-testid="convert-category"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Contact Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={convertForm.contact_name}
                onChange={e => setConvertForm(f => ({ ...f, contact_name: e.target.value }))}
                placeholder="Owner / Manager name"
                data-testid="convert-contact-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)} disabled={converting}>Cancel</Button>
            <Button onClick={handleConvertToSmb} disabled={converting} data-testid="convert-submit-btn">
              {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Convert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMBInboxPage;
